import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Loader2, ListTodo, Repeat, Calendar, Edit3, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const RECURRING = [
  { value: '', label: 'No' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

export default function TodosPanel({ api }) {
  const [lists, setLists] = useState([]);
  const [todos, setTodos] = useState([]);
  const [activeList, setActiveList] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const [editing, setEditing] = useState(null); // todo being edited
  const [creating, setCreating] = useState(false);
  const [members, setMembers] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get('/ceo-life/lists'),
        api.get('/ceo-life/todos'),
        api.get('/ceo-life/access'),
      ]);
      setLists(a.data || []);
      setTodos(b.data || []);
      setMembers(c.data || []);
    } catch { toast.error('Failed to load to-dos'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = todos.filter(t => {
    if (!showDone && t.done) return false;
    if (activeList !== 'all' && t.list_id !== activeList) return false;
    return true;
  });

  const toggleDone = async (t) => {
    try {
      const { data } = await api.put(`/ceo-life/todos/${t.id}`, { done: !t.done });
      setTodos(prev => {
        const map = new Map(prev.map(x => [x.id, x]));
        map.set(data.id, data);
        // recurring may have spawned a new one — refetch quietly
        return Array.from(map.values());
      });
      if (!t.done && t.recurring) {
        fetchAll(); // pull in the spawned one
      }
    } catch { toast.error('Update failed'); }
  };

  const removeTodo = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.delete(`/ceo-life/todos/${id}`);
      setTodos(prev => prev.filter(t => t.id !== id));
    } catch { toast.error('Delete failed'); }
  };

  const pushTodoToGoogle = async (t) => {
    if (!t.due_date) { toast.error('Set a due date first'); return; }
    try {
      const { data } = await api.post('/calendar/push/todo', { todo_id: t.id });
      toast.success(t.google_event_id ? 'Updated on Google Calendar' : 'Pushed to Google Calendar', {
        action: data.html_link ? { label: 'Open', onClick: () => window.open(data.html_link, '_blank') } : undefined,
      });
      setTodos(prev => prev.map(x => x.id === t.id ? { ...x, google_event_id: data.id, google_html_link: data.html_link } : x));
    } catch (err) {
      const msg = err.response?.data?.detail || 'Push failed';
      toast.error(typeof msg === 'string' ? msg : 'Push failed — connect Google Calendar first?');
    }
  };

  const saveTodo = async (form) => {
    try {
      if (form.id) {
        const { data } = await api.put(`/ceo-life/todos/${form.id}`, form);
        setTodos(prev => prev.map(t => t.id === data.id ? data : t));
        toast.success('Updated');
      } else {
        const { data } = await api.post('/ceo-life/todos', form);
        setTodos(prev => [data, ...prev]);
        toast.success('Added');
      }
      setEditing(null);
      setCreating(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const addList = async () => {
    const name = window.prompt('New list name?');
    if (!name) return;
    try {
      const { data } = await api.post('/ceo-life/lists', { name });
      setLists(p => [...p, data]);
      setActiveList(data.id);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;

  return (
    <div className="space-y-5" data-testid="ceo-todos">
      {/* Lists row */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setActiveList('all')}
          className={`ceo-tab-btn ${activeList === 'all' ? 'active' : ''}`}
          style={{ fontSize: 12, padding: '6px 12px' }}
          data-testid="ceo-list-all"
        >All</button>
        {lists.map(l => (
          <button
            key={l.id}
            onClick={() => setActiveList(l.id)}
            className={`ceo-tab-btn ${activeList === l.id ? 'active' : ''}`}
            style={{ fontSize: 12, padding: '6px 12px' }}
            data-testid={`ceo-list-${l.id}`}
          >
            <span className="ceo-priority-dot normal" style={{ background: l.color || 'var(--accent)', boxShadow: 'none' }} />
            {l.name}
          </button>
        ))}
        <button onClick={addList} className="ceo-btn ceo-btn-ghost" style={{ padding: '6px 10px', fontSize: 12 }} data-testid="ceo-add-list">
          <Plus className="h-3 w-3" /> List
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-dim)' }}>
            <input type="checkbox" className="ceo-checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} data-testid="ceo-show-done" />
            Show completed
          </label>
        </div>
        <button onClick={() => setCreating(true)} className="ceo-btn ceo-btn-primary" data-testid="ceo-todo-add-btn">
          <Plus className="h-4 w-4" /> Add task
        </button>
      </div>

      {/* New / edit form (inline) */}
      {(creating || editing) && (
        <TodoForm
          initial={editing || {}}
          lists={lists}
          members={members}
          onCancel={() => { setCreating(false); setEditing(null); }}
          onSave={saveTodo}
        />
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <div className="ceo-empty" data-testid="ceo-todos-empty">
          <ListTodo className="ceo-empty-icon h-9 w-9" />
          <p className="text-sm">{showDone ? 'Nothing here yet.' : 'All caught up. Add a task to get started.'}</p>
        </div>
      ) : (
        <div className="ceo-card-elevated p-2" data-testid="ceo-todos-list">
          {filtered.map(t => (
            <TodoRow
              key={t.id}
              todo={t}
              lists={lists}
              onToggle={() => toggleDone(t)}
              onEdit={() => setEditing(t)}
              onDelete={() => removeTodo(t.id)}
              onPushGoogle={() => pushTodoToGoogle(t)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TodoRow({ todo, lists, onToggle, onEdit, onDelete, onPushGoogle }) {
  const list = lists.find(l => l.id === todo.list_id);
  const synced = !!todo.google_event_id;
  const canPush = !!todo.due_date;
  return (
    <div className={`ceo-todo-row ${todo.done ? 'done' : ''}`} data-testid={`ceo-todo-${todo.id}`}>
      <input
        type="checkbox"
        className="ceo-checkbox"
        checked={!!todo.done}
        onChange={onToggle}
        data-testid={`ceo-todo-toggle-${todo.id}`}
        style={{ marginTop: 2 }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`ceo-priority-dot ${todo.priority || 'normal'}`} />
          <span className="ceo-todo-title">{todo.title}</span>
          {todo.recurring && (
            <span className="ceo-pill" style={{ padding: '1px 6px', fontSize: 9 }} title={`Recurring ${todo.recurring}`}>
              <Repeat className="h-2.5 w-2.5" />{todo.recurring}
            </span>
          )}
          {list && (
            <span className="ceo-pill" style={{ padding: '1px 6px', fontSize: 9, background: 'transparent' }}>
              <span className="ceo-priority-dot normal" style={{ width: 5, height: 5, background: list.color || 'var(--accent)', boxShadow: 'none' }} />
              {list.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-[11px]" style={{ color: 'var(--text-low)' }}>
          {todo.due_date && <span className="flex items-center gap-1"><Calendar className="h-2.5 w-2.5" />{formatDate(todo.due_date)}</span>}
          {todo.assigned_to && <span>→ {todo.assigned_to}</span>}
          {todo.notes && <span className="truncate flex-1 italic">{todo.notes}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 hover:opacity-100 transition-opacity" style={{ opacity: 1 }}>
        {canPush && (
          <button
            onClick={onPushGoogle}
            className="ceo-btn ceo-btn-ghost"
            style={{ padding: '4px 6px', color: synced ? '#4CD964' : undefined }}
            title={synced ? 'Update on Google Calendar' : 'Push to Google Calendar'}
            data-testid={`ceo-todo-push-google-${todo.id}`}
          >
            {synced ? <Check className="h-3 w-3" /> : <Calendar className="h-3 w-3" />}
          </button>
        )}
        <button onClick={onEdit} className="ceo-btn ceo-btn-ghost" style={{ padding: '4px 6px' }} data-testid={`ceo-todo-edit-${todo.id}`}>
          <Edit3 className="h-3 w-3" />
        </button>
        <button onClick={onDelete} className="ceo-btn ceo-btn-danger" style={{ padding: '4px 6px' }} data-testid={`ceo-todo-delete-${todo.id}`}>
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function TodoForm({ initial, lists, members, onCancel, onSave }) {
  const [form, setForm] = useState({
    id: initial.id || '',
    title: initial.title || '',
    notes: initial.notes || '',
    due_date: initial.due_date || '',
    priority: initial.priority || 'normal',
    assigned_to: initial.assigned_to || '',
    list_id: initial.list_id || (lists[0]?.id || ''),
    recurring: initial.recurring || '',
  });
  return (
    <div className="ceo-card-elevated p-4 space-y-3" data-testid="ceo-todo-form">
      <input
        autoFocus
        className="ceo-input"
        placeholder="What needs to happen?"
        value={form.title}
        onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
        data-testid="ceo-todo-title-input"
      />
      <textarea
        rows={2}
        className="ceo-textarea"
        placeholder="Notes (optional)"
        value={form.notes}
        onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
        data-testid="ceo-todo-notes-input"
      />
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div>
          <label className="ceo-eyebrow">Priority</label>
          <select className="ceo-select" value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} data-testid="ceo-todo-priority">
            {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="ceo-eyebrow">Due</label>
          <input type="date" className="ceo-input" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} data-testid="ceo-todo-due" />
        </div>
        <div>
          <label className="ceo-eyebrow">Recurring</label>
          <select className="ceo-select" value={form.recurring} onChange={e => setForm(p => ({ ...p, recurring: e.target.value }))} data-testid="ceo-todo-recurring">
            {RECURRING.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="ceo-eyebrow">List</label>
          <select className="ceo-select" value={form.list_id} onChange={e => setForm(p => ({ ...p, list_id: e.target.value }))} data-testid="ceo-todo-list-select">
            {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div>
          <label className="ceo-eyebrow">Assigned to</label>
          <select className="ceo-select" value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))} data-testid="ceo-todo-assigned">
            <option value="">— No one —</option>
            {members.map(m => <option key={m.id} value={m.name || m.email}>{m.name || m.email}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button onClick={onCancel} className="ceo-btn ceo-btn-ghost" data-testid="ceo-todo-cancel"><X className="h-3 w-3" />Cancel</button>
        <button onClick={() => form.title.trim() && onSave(form)} disabled={!form.title.trim()} className="ceo-btn ceo-btn-primary" data-testid="ceo-todo-save">
          <Check className="h-3 w-3" />{form.id ? 'Update' : 'Add'}
        </button>
      </div>
    </div>
  );
}

function formatDate(d) {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return d; }
}
